import { useTranslation } from '@rhino-project/core/i18n';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationPreviousIcon,
  PaginationNext,
  PaginationNextIcon,
  PaginationEllipsis
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useModelIndexContext } from '@rhino-project/core/hooks';
import { useGlobalComponentForModel } from '@rhino-project/core/hooks';
import { useMemo } from 'react';

function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | '...')[] = [1];

  if (current > 3) pages.push('...');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - 2) pages.push('...');

  pages.push(total);

  return pages;
}

export const ModelPagerBase = (props: Record<string, unknown>) => {
  const { t } = useTranslation();
  const { totalPages, setPage, page } = useModelIndexContext();
  const pages = useMemo(
    () => getPageNumbers(page, totalPages || 0),
    [page, totalPages]
  );

  if (!totalPages) return null;

  return (
    <Pagination aria-label={t('rhino.frontend.messages.pagination')} {...props}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            aria-label={t('rhino.frontend.messages.first_page')}
            onPress={() => setPage(1)}
            isDisabled={page <= 1}
          >
            <Icon icon="bi:chevron-bar-left" />
          </PaginationPrevious>
        </PaginationItem>

        <PaginationItem>
          <PaginationPrevious
            aria-label={t('rhino.frontend.messages.previous_page')}
            onPress={() => setPage(Math.max(1, page - 1))}
            isDisabled={page <= 1}
          >
            <PaginationPreviousIcon />
          </PaginationPrevious>
        </PaginationItem>

        {pages.map((n, i) =>
          n === '...' ? (
            <PaginationItem
              key={
                // eslint-disable-next-line @eslint-react/no-array-index-key
                `ellipsis-${i}`
              }
            >
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={n}>
              <PaginationLink
                aria-label={t('rhino.frontend.messages.page_number', {
                  page: n
                })}
                isActive={n === page}
                onPress={() => setPage(n as number)}
              >
                {n}
              </PaginationLink>
            </PaginationItem>
          )
        )}

        <PaginationItem>
          <PaginationNext
            aria-label={t('rhino.frontend.messages.next_page')}
            onPress={() => setPage(Math.min(totalPages, page + 1))}
            isDisabled={page >= totalPages}
          >
            <PaginationNextIcon />
          </PaginationNext>
        </PaginationItem>

        <PaginationItem>
          <PaginationNext
            aria-label={t('rhino.frontend.messages.last_page')}
            onPress={() => setPage(totalPages)}
            isDisabled={page >= totalPages}
          >
            <Icon icon="bi:chevron-bar-right" />
          </PaginationNext>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

export const ModelPager = (props: Record<string, unknown>) =>
  useGlobalComponentForModel('ModelPager', ModelPagerBase, props);
